import { findIndex, isEqual, set } from 'lodash';

import type { AuditLineItemV5, ChangeLogUnionObject, SimpleChangeValue } from '../../../apis/graphql';
import { formatTimeDetail } from '../../../utils/formatting';
import { ROWS_TO_IGNORE } from '../../ChangeLog/constants';
import { modifyDefinition, modifySchedule, modifyTargetingRule } from '../../ChangeLog/restructureData/fields';
import {
  camelizeObjectKeys,
  getFieldName,
  getParentFieldName,
  removeEmptyArrayElements,
  setPayloadToNull,
} from '../../ChangeLog/restructureData/util';
import {
  getArrayChangeTableProps,
  getArrayPropertyWithIndex,
  getChangesPluralString,
  isFieldArrayChange,
} from '../../ChangeLog/utils';
import type {
  AuditProps,
  ChangeLogEntry,
  ChangeLogNode,
  FilteredChangeLogEntry,
  GroupedChanges,
  NormalizedChanges,
} from '../types';

export const hasDuplicates = (newValue: SimpleChangeValue, oldValue: SimpleChangeValue): boolean => {
  const oldAndNewAreSame = isEqual(newValue.payload, oldValue.payload);

  const newValueIsDirty =
    newValue.payload === null ||
    typeof newValue.payload === 'undefined' ||
    (Array.isArray(newValue.payload) && !newValue.payload.length);

  const oldValueIsDirty =
    oldValue.payload === null ||
    typeof oldValue.payload === 'undefined' ||
    (Array.isArray(oldValue.payload) && !oldValue.payload.length);

  return oldAndNewAreSame || (newValueIsDirty && oldValueIsDirty);
};

export const getChangesLengthWithoutDuplicates = (changeList: ChangeLogEntry[]): number =>
  changeList.length
    ? changeList.filter(
        (value) =>
          !ROWS_TO_IGNORE.includes(value.fieldName) &&
          !hasDuplicates(value.newValue as SimpleChangeValue, value.oldValue as SimpleChangeValue)
      ).length
    : 0;

const getGroupedChangesByIndex = (acc: GroupedChanges, next: ChangeLogEntry): GroupedChanges => {
  if (isFieldArrayChange(next.fieldName)) {
    const { index, property } = getArrayPropertyWithIndex(next.fieldName);

    if (index === undefined && Array.isArray(next.newValue?.payload)) {
      return {
        ...acc,
        arrayGroupedChanges: {
          ...acc.arrayGroupedChanges,
        },
      };
    }

    const arrayPropertyNameAndIndex = `${property}[${index}]`;
    const oldArrayChanges = acc.arrayGroupedChanges[arrayPropertyNameAndIndex] || [];

    return {
      ...acc,
      arrayGroupedChanges: {
        ...acc.arrayGroupedChanges,
        [arrayPropertyNameAndIndex]: [...oldArrayChanges, next],
      },
    };
  }

  return { ...acc, notArrayChanges: [...acc.notArrayChanges, next] };
};

export const divideChangesByType = (node: ChangeLogNode): { audits: GroupedChanges } => {
  const audits = node.changeList.reduce(getGroupedChangesByIndex, {
    notArrayChanges: [],
    arrayGroupedChanges: {},
  });

  return {
    audits,
  };
};

export const prepareRestructuredChangeList = (changeList: ChangeLogEntry[]): ChangeLogEntry[] => {
  const restructuredChangeList: ChangeLogEntry[] = [];

  const oldPayloadObject: Record<string, string> = {};
  const newPayloadObject: Record<string, string> = {};

  changeList.forEach((change) => {
    const [fieldName, subFieldName] = getParentFieldName(change.fieldName);
    set(oldPayloadObject, fieldName, change.oldValue.payload);
    set(newPayloadObject, fieldName, change.newValue.payload);
    const newChangeObject = {
      fieldName: getFieldName(fieldName, subFieldName),
      oldValue: {
        action: change.oldValue.action,
        payload: setPayloadToNull(removeEmptyArrayElements(oldPayloadObject[fieldName])),
        __typename: change.oldValue.__typename,
      },
      newValue: {
        action: change.newValue.action,
        payload: setPayloadToNull(removeEmptyArrayElements(newPayloadObject[fieldName])),
        __typename: change.newValue.__typename,
      },
    };

    const index = findIndex(restructuredChangeList, { fieldName });

    if (index === -1) {
      restructuredChangeList.push(newChangeObject);
    } else {
      restructuredChangeList.splice(index, 1, newChangeObject);
    }
  });

  return restructuredChangeList;
};

export const restructureData = (
  previousObject: ChangeLogUnionObject | null,
  object: ChangeLogUnionObject,
  changeList: ChangeLogEntry[]
): ChangeLogEntry[] => {
  const restructuredChangeList = prepareRestructuredChangeList(changeList);

  const camelCasedRestructuredChangeList = restructuredChangeList.map((change) => camelizeObjectKeys(change));

  const scheduleBefore = (previousObject as AuditLineItemV5)?.schedule;
  const scheduleAfter = (object as AuditLineItemV5)?.schedule;

  camelCasedRestructuredChangeList.forEach((change, index) => {
    const [fieldName] = getParentFieldName(change.fieldName);
    switch (fieldName) {
      case 'schedule':
        modifySchedule({ change, restructuredChangeList, scheduleBefore, scheduleAfter });
        break;

      case 'definition':
        modifyDefinition(change, restructuredChangeList);
        break;

      case 'targeting-rule':
        modifyTargetingRule(restructuredChangeList[index], object, null);
        break;
    }
  });
  return restructuredChangeList;
};

export const getDefaultChangesChangeLogTableProps = (
  object: ChangeLogUnionObject,
  changeList?: ChangeLogEntry[]
): AuditProps => ({
  changes: changeList ? restructureData(null, object, changeList) : [],
  subHeaderTitle: '',
  isLineThrough: false,
});

export const getChangeLogTablesProps = ({ node }: { node: ChangeLogNode }): { auditProps: AuditProps[] } => {
  const {
    audits: { notArrayChanges, arrayGroupedChanges },
  } = divideChangesByType(node);
  let auditProps: AuditProps[] = [];
  if (notArrayChanges.length > 0) {
    auditProps.push(getDefaultChangesChangeLogTableProps(node.object, notArrayChanges));
  }
  let arrayTablesProps = Object.entries(arrayGroupedChanges).map(([propertyName, changes]) =>
    getArrayChangeTableProps(changes, node?.object, propertyName)
  );
  auditProps = auditProps.concat(arrayTablesProps);
  return { auditProps };
};

export const getNormalizedChanges = ({ node }: { node: ChangeLogNode }): NormalizedChanges => {
  const tablesProps = getChangeLogTablesProps({
    node,
  });
  let changesLength = getChangesLengthWithoutDuplicates(
    tablesProps.auditProps.reduce<ChangeLogEntry[]>((acc, { changes }) => [...acc, ...changes], [])
  );
  const changesPlural = getChangesPluralString(changesLength);
  return {
    audits: {
      tablesProps: tablesProps.auditProps,
      changesPlural,
      changesLength,
    },
  };
};

export const useNotificationsChangeLogList = ({
  allEvents,
}: {
  allEvents: ChangeLogNode[];
}): { filteredChangeLogList: FilteredChangeLogEntry[] } => {
  const filteredChangeLogList = allEvents?.map((node, index) => {
    const { modifiedBy, modifiedDate } = node;
    const { audits } = getNormalizedChanges({
      node,
    });
    const dateFormat = formatTimeDetail(modifiedDate);
    return {
      audits,
      id: node?.id,
      dateFormat,
      modifiedBy,
      originalIndex: index,
    };
  });
  const filteredChangeLogListWithChanges = filteredChangeLogList?.filter((log) => log.audits.changesLength);
  return { filteredChangeLogList: filteredChangeLogListWithChanges };
};
